import axios from 'axios';
import { API_BASE_URL as BASE } from './apiConfig';
import { EnterpriseContract } from './planApi';

const API_BASE_URL = `${BASE}/api/dashboard`;

// ============================================
// 인터페이스 정의
// ============================================

export interface DashboardSummary {
  totalProcessed: number;
  todayProcessed: number;
  completionRate: number;
  apiUsageCount: number;
  aiCompletedCount?: number;
  aiCompletionRate?: number;
  period: {
    from: string;
    to: string;
  };
}

export interface ServiceStats {
  period: string;
  services: ServiceStatsItem[];
  total: ServiceStatsSummary;
}

export interface ServiceStatsItem {
  serviceType: 'WEBSITE_CRAWLING' | 'IMAGE_URL' | 'IMAGE_UPLOAD';
  serviceName: string;
  totalCount: number;
  completedCount: number;
  pendingCount: number;
  failedCount: number;
  completionRate: number;
}

export interface ServiceStatsSummary {
  totalCount: number;
  completedCount: number;
  pendingCount: number;
  failedCount: number;
  completionRate: number;
}

export interface TodayStats {
  date: string;
  totalProcessed: number;
  serviceBreakdown: ServiceBreakdownItem[];
  hourlyStats: HourlyStatItem[];
}

export interface ServiceBreakdownItem {
  serviceType: 'WEBSITE_CRAWLING' | 'IMAGE_URL' | 'IMAGE_UPLOAD';
  count: number;
}

export interface HourlyStatItem {
  hour: number;
  count: number;
}

export interface TrendData {
  period: string;
  startDate: string;
  endDate: string;
  dataPoints: TrendDataPoint[];
}

export interface TrendDataPoint {
  date: string;
  websiteCrawling: number;
  imageUrl: number;
  imageUpload: number;
  total: number;
}

export interface CompletionRateData {
  period: string;
  overall: CompletionStats;
  byService: CompletionStatsByService[];
}

export interface CompletionStats {
  totalImages: number;
  completed: number;
  pending: number;
  failed: number;
  completionRate: number;
}

export interface CompletionStatsByService {
  serviceType: 'WEBSITE_CRAWLING' | 'IMAGE_URL' | 'IMAGE_UPLOAD';
  totalImages: number;
  completed: number;
  pending: number;
  failed: number;
  completionRate: number;
}

export interface AiUsageStatus {
  userId: string;
  plan: string;  // 백엔드에서 한글 플랜명으로 올 수 있음 (무료, 스타트, 프로페셔널, 엔터프라이즈)
  used: number;
  limit: number;
  remaining: number;
  canUse: boolean;
  suspended: boolean;
  dailyUsed: number | null;
  dailyLimit: number | null;
  isTeamContext: boolean;
  teamId: number | null;
  teamName: string | null;
  role: 'OWNER' | 'ADMIN' | 'MEMBER' | null;
  teamLimit: number | null;
  teamTotalUsed: number | null;
  enterpriseContract?: EnterpriseContract | null;
}

export interface BillingPeriod {
  startDate: string;
  endDate: string;
  nextBillingDate: string;
}

// API 응답 공통 래퍼
interface ApiResponse<T> {
  success: boolean;
  data: T;
}


// ============================================
// API 함수들
// ============================================

export const dashboardApi = {
  /**
   * 통계 요약 조회
   * @param userId SUPER_ADMIN만 사용. 특정 사용자 조회 시 전달
   */
  getSummary: async (userId?: string): Promise<DashboardSummary> => {
    const params = userId ? { userId } : {};
    const response = await axios.get<ApiResponse<DashboardSummary>>(
      `${API_BASE_URL}/summary`,
      { params }
    );
    return response.data.data;
  },

  /**
   * AI 사용량 상태 조회 (팀 컨텍스트 포함)
   */
  getAiUsageStatus: async (): Promise<AiUsageStatus> => {
    const response = await axios.get<AiUsageStatus>(
      `${BASE}/api/dashboard/ai-usage-status`
    );
    return response.data;
  },

  /**
   * 청구 기간 조회
   */
  getBillingPeriod: async (): Promise<BillingPeriod | null> => {
    const response = await axios.get<{ success: boolean; data: BillingPeriod | null }>(
      `${BASE}/api/dashboard/billing-period`
    );
    return response.data.data;
  },

  /**
   * 서비스별 처리량 조회
   * @param userId SUPER_ADMIN만 사용
   * @param period TODAY, WEEK, MONTH, ALL (기본값: ALL)
   */
  getServiceStats: async (
    userId?: string,
    period: 'TODAY' | 'WEEK' | 'MONTH' | 'ALL' = 'ALL'
  ): Promise<ServiceStats> => {
    const params = { period, ...(userId && { userId }) };
    const response = await axios.get<ApiResponse<ServiceStats>>(
      `${API_BASE_URL}/service-stats`,
      { params }
    );
    return response.data.data;
  },

  /**
   * 금일 처리량 상세 조회
   * @param userId SUPER_ADMIN만 사용
   */
  getTodayStats: async (userId?: string): Promise<TodayStats> => {
    const params = userId ? { userId } : {};
    const response = await axios.get<ApiResponse<TodayStats>>(
      `${API_BASE_URL}/today-stats`,
      { params }
    );
    return response.data.data;
  },

  /**
   * 주간/월간 트렌드 조회
   * @param period WEEK (7일), MONTH (30일), QUARTER (90일) - 필수
   * @param userId SUPER_ADMIN만 사용
   */
  getTrend: async (
    period: 'WEEK' | 'MONTH' | 'QUARTER',
    userId?: string
  ): Promise<TrendData> => {
    const params = { period, ...(userId && { userId }) };
    const response = await axios.get<ApiResponse<TrendData>>(
      `${API_BASE_URL}/trend`,
      { params }
    );
    return response.data.data;
  },

  /**
   * ALT 생성 완료율 상세 조회
   * @param userId SUPER_ADMIN만 사용
   * @param period TODAY, WEEK, MONTH, ALL (기본값: ALL)
   */
  getCompletionRate: async (
    userId?: string,
    period: 'TODAY' | 'WEEK' | 'MONTH' | 'ALL' = 'ALL'
  ): Promise<CompletionRateData> => {
    const params = { period, ...(userId && { userId }) };
    const response = await axios.get<ApiResponse<CompletionRateData>>(
      `${API_BASE_URL}/completion-rate`,
      { params }
    );
    return response.data.data;
  },
};

// ============================================
// AI 사용량 이력 관련 인터페이스 & API
// ============================================

export interface AiUsageSummary {
  targetUserId: string;
  currentYearMonth: string;
  currentMonthAiCount: number;
  totalAiCount: number;
  bySourceType: {
    WEBSITE_CRAWLING: number;
    IMAGE_URL: number;
    IMAGE_UPLOAD: number;
  };
  subscriptionPlan: string;  // 백엔드에서 한글 플랜명으로 올 수 있음 (무료, 스타트, 프로페셔널, 엔터프라이즈)
  // B/E 추가 예정 (P0-T03): 서버 쪽 집계 값 제공 시 사용
  monthlyLimit?: number;   // B/E: GET /api/ai-usage/summary 에 추가 요청
  remaining?: number;      // B/E: GET /api/ai-usage/summary 에 추가 요청
  usageRate?: number;      // B/E: 0~100 (백분율), GET /api/ai-usage/summary 에 추가 요청
}

export interface AiUsageMonthly {
  targetUserId: string;
  monthly: { yearMonth: string; count: number }[];
}

export interface AiUsageTopUser {
  userId: string;
  yearMonth: string;
  count: number;
}

export const aiUsageApi = {
  /** AI 사용량 요약 (본인 또는 SUPER_ADMIN이 타인 조회) */
  getSummary: async (userId?: string): Promise<AiUsageSummary> => {
    const params = userId ? { userId } : {};
    const response = await axios.get<ApiResponse<AiUsageSummary>>(
      `${API_BASE_URL}/ai-usage/summary`,
      { params }
    );
    return response.data.data;
  },

  /** 최근 6개월 월별 AI 사용량 (차트용) */
  getMonthly: async (userId?: string): Promise<AiUsageMonthly> => {
    const params = userId ? { userId } : {};
    const response = await axios.get<ApiResponse<AiUsageMonthly>>(
      `${API_BASE_URL}/ai-usage/monthly`,
      { params }
    );
    return response.data.data;
  },

  /** 이번 달 전체 사용자 AI 사용량 TOP (SUPER_ADMIN 전용) */
  getTopUsers: async (): Promise<AiUsageTopUser[]> => {
    const response = await axios.get<ApiResponse<AiUsageTopUser[]>>(
      `${API_BASE_URL}/ai-usage/top-users`
    );
    return response.data.data;
  },
};
